import type Phaser from "phaser";
import { LOOK_SHEETS_DIR, STARTER_WORN, lookOf, newWireLook, validateLookManifest, wireLookOf, type Appearance, type Looks, type WireLook, type LookManifest } from "odyssey-shared";
import type { CharacterFigure } from "../character.ts";
import type { LookFigures } from "./figures.ts";
import { LookCache, type LookCacheStats } from "./look-cache.ts";
import { crowdLook } from "./look-plan.ts";
import { LookSprite } from "./look-sprite.ts";

/**
 * The composed figures of a game page (ADR-094 decisions 14 to 16, note 80): the one look cache of the page and the
 * figures drawn from it. Every player is one (`player`), wearing the fallback look until the page tells it the look
 * its handle holds (`wear`). Under the developer flag `?looks=crowd` (`crowd-gate.ts`) every creature is one too, in a
 * look derived from its handle, so the stress world's crowd rows stand in for a crowd of players with nothing on the
 * wire; without the flag a creature is drawn from its own sheet as before.
 *
 * The page loads the look sheets' pointer and the fallback look's sheets before it joins (`start`, `loadSheets`). A
 * page that cannot have them (no pointer under its asset base, a malformed one, or a fallback sheet that does not
 * load) warns once and has no cache: `player` then answers null and the scene draws its plain figure.
 */

/**
 * Where the look sheets' pointer lies under the asset base.
 */
export const LOOK_POINTER = `${LOOK_SHEETS_DIR}/current.json`;

/**
 * Fetches and validates the look sheets' pointer.
 *
 * @param base The asset base the page loads from.
 * @param fetcher What fetches; the page's `fetch` unless a test hands in another.
 * @returns The pointer.
 * @throws Error naming the address when it does not answer, answers with an error or does not validate.
 */
export async function loadLookPointer(base: string, fetcher: (url: string, init: RequestInit) => Promise<Response> = (url, init) => fetch(url, init)): Promise<ReturnType<typeof validateLookManifest>> {
  const url = `${base}/${LOOK_POINTER}`;
  let response: Response;
  try {
    response = await fetcher(url, { cache: "no-cache" });
  } catch (error) {
    throw new Error(`${url} did not answer (${error instanceof Error ? error.message : String(error)})`);
  }
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return validateLookManifest(await response.json());
}

/**
 * The composed figures of one page.
 */
export class PageFigures implements LookFigures {
  private cache: LookCache | null = null;
  private scene: Phaser.Scene | null = null;
  private asked = false;
  private readonly look = newWireLook();

  /**
   * @param looks The page's looks table.
   * @param crowd Whether creatures are composed too, the developer flag's answer.
   * @param makeCache What makes the cache from a loaded pointer; the page's unless a test hands in another.
   */
  public constructor(
    private readonly looks: Looks,
    private readonly crowd: boolean,
    private readonly makeCache: (scene: Phaser.Scene, base: string, looks: Looks) => Promise<LookCache> = async (scene, base, table) => new LookCache({ textures: scene.textures, renderer: scene.game.renderer }, base, table, await loadLookPointer(base)),
  ) {}

  /**
   * Loads the pointer and makes the cache, which asks for the fallback look's sheets, and for the own look's when it
   * is given: the look is named and let go at once, which asks for its sheets and keeps no block, so the own figure
   * that wears it later finds them loaded. A pointer that does not load, or a malformed one, is a failure: the page
   * has no other figure to draw a player with (ADR-094 note 88), so the world boot fails on its shell and its Retry
   * starts again. Asked again once started, it does nothing.
   *
   * @param scene The game scene.
   * @param base The asset base the page loads from.
   * @param own The page's own look, or null or absent when unknown.
   * @throws Error saying why players cannot be drawn, when the cache could not be made.
   */
  public async start(scene: Phaser.Scene, base: string, own: Appearance | null = null): Promise<void> {
    if (this.asked) {
      return;
    }
    this.asked = true;
    try {
      this.cache = await this.makeCache(scene, base, this.looks);
      this.scene = scene;
      if (own !== null) {
        wireLookOf(this.looks, lookOf(own, STARTER_WORN), this.look);
        this.cache.release(this.cache.retain(this.look));
      }
    } catch (error) {
      this.destroy();
      this.asked = false;
      throw new Error(`players cannot be drawn: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Whether the looks are started: the pointer is read and the cache made.
   *
   * @returns True from a start that succeeded until a failure or `destroy`.
   */
  public get started(): boolean {
    return this.cache !== null;
  }

  /**
   * How many piece sheets the page loads before it joins: the fallback look's.
   *
   * @returns The count, 0 without a cache.
   */
  public get bootSheets(): number {
    return this.cache?.fallbackSheets ?? 0;
  }

  /**
   * How many of those sheets are loaded now.
   *
   * @returns The count.
   */
  public get bootSheetsLoaded(): number {
    return this.cache?.fallbackSheetsLoaded ?? 0;
  }

  /**
   * Waits for the fallback look's sheets. If one does not load the cache is removed and the wait fails, as a missing
   * pointer fails `start`: every player is drawn in the fallback look until its own is composed, so without it there
   * is nothing to draw. A start after it asks for everything again.
   *
   * @throws Error saying why players cannot be drawn, when a sheet of the fallback look did not load.
   */
  public async loadSheets(): Promise<void> {
    const cache = this.cache;
    if (cache === null || (await cache.fallbackLoads()) || this.cache !== cache) {
      return;
    }
    this.destroy();
    this.asked = false;
    throw new Error("players cannot be drawn: a sheet of the fallback look did not load");
  }

  /**
   * A composed figure for a player, in the fallback look until `wear` says another.
   *
   * @param depthOf Depth for a feet position in pixels, x then y.
   * @returns The figure, or null when the looks did not load.
   */
  public player(depthOf: (feetX: number, feetY: number) => number): CharacterFigure | null {
    const cache = this.cache;
    const scene = this.scene;
    if (cache === null || scene === null) {
      return null;
    }
    return new LookSprite(scene, cache, depthOf);
  }

  /**
   * Tells a composed figure the look it wears.
   *
   * @param figure The figure; anything but a composed one is left as it is.
   * @param look The look as it arrived, or null for a figure that holds none, which wears the fallback look.
   */
  public wear(figure: CharacterFigure | null | undefined, look: Readonly<WireLook> | null): void {
    const cache = this.cache;
    if (cache !== null && figure instanceof LookSprite) {
      figure.wear(look ?? cache.fallback);
    }
  }

  /**
   * Whether a figure is drawn as itself.
   *
   * @param figure The figure.
   * @returns False only for a composed figure whose own look's block is not composed yet.
   */
  public dressed(figure: CharacterFigure | null | undefined): boolean {
    return !(figure instanceof LookSprite) || figure.dressed;
  }

  /**
   * A composed figure for a creature, in the look its handle derives, under the developer flag.
   *
   * @param handle The creature's handle.
   * @param depthOf Depth for a feet position in pixels, x then y.
   * @returns The figure, or null without the flag or when the looks did not load.
   */
  public creature(handle: number, depthOf: (feetX: number, feetY: number) => number): CharacterFigure | null {
    const cache = this.cache;
    const scene = this.scene;
    if (!this.crowd || cache === null || scene === null) {
      return null;
    }
    return new LookSprite(scene, cache, depthOf, crowdLook(this.looks, handle, this.look));
  }

  /**
   * The one head height of every composed figure.
   *
   * @returns The layout's head height, or null when the looks did not load.
   */
  public head(): number | null {
    return this.cache?.layout.head ?? null;
  }

  /**
   * The head height of a composed creature.
   *
   * @returns The layout's head height, or null without the flag or when the looks did not load.
   */
  public creatureHead(): number | null {
    return this.crowd ? this.head() : null;
  }

  /**
   * Whether composed figures can show: the fallback look is drawn. True without a cache, which has none to wait for.
   *
   * @returns False only while a cache waits for its fallback block.
   */
  public get ready(): boolean {
    return this.cache?.ready ?? true;
  }

  /**
   * Opens a frame: composes what the frame before planned.
   *
   * @param focusX Where the page's own character is drawn, in world pixels.
   * @param focusY The same, down.
   * @param nowMs The time in ms.
   */
  public frame(focusX: number, focusY: number, nowMs: number): void {
    this.cache?.settle(focusX, focusY, nowMs);
  }

  /**
   * Keeps a composed figure's look from giving up its block this frame, and puts a look it waits to wear before
   * every unpinned one for a block.
   *
   * @param figure The figure; anything but a composed one is ignored.
   */
  public pin(figure: CharacterFigure | null | undefined): void {
    if (figure instanceof LookSprite) {
      this.cache?.pin(figure.look);
      if (figure.waiting >= 0) {
        this.cache?.pin(figure.waiting);
      }
    }
  }

  /**
   * The look sheets' pointer the page loaded.
   *
   * @returns The pointer, or null when the looks did not load.
   */
  public get pointer(): LookManifest | null {
    return this.cache?.manifest ?? null;
  }

  /**
   * What the look cache holds.
   *
   * @returns The readings, or null when the looks did not load.
   */
  public get stats(): LookCacheStats | null {
    return this.cache?.stats ?? null;
  }

  /**
   * The look cache, for a rig that composes or reads a block itself.
   *
   * @returns The cache, or null when the looks did not load.
   */
  public get lookCache(): LookCache | null {
    return this.cache;
  }

  /**
   * Removes the cache's textures.
   */
  public destroy(): void {
    this.cache?.destroy();
    this.cache = null;
    this.scene = null;
  }
}

/**
 * The composed figures for a page.
 *
 * @param looks The page's looks table.
 * @param crowd Whether creatures are composed too (`crowdLooksAllowed`).
 * @returns What the scene asks of them.
 */
export function pageFigures(looks: Looks, crowd: boolean): LookFigures {
  return new PageFigures(looks, crowd);
}
